import React, { useState } from 'react';
import { useFirebase } from '../../hooks/useFirebase';
import './SymptomLogger.css';

function SymptomLogger() {
  const { migraineLogService, loading, error, clearError } = useFirebase();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().split(' ')[0].slice(0, 5),
    severity: 5,
    duration: '',
    location: [],
    triggers: [],
    symptoms: [],
    notes: ''
  });

  const [showSuccess, setShowSuccess] = useState(false);

  const locationOptions = [
    'Front of head', 'Back of head', 'Left side', 'Right side', 
    'Behind eyes', 'Temples', 'Entire head'
  ];

  const triggerOptions = [
    'Stress', 'Lack of sleep', 'Bright lights', 'Loud noises',
    'Weather change', 'Certain foods', 'Alcohol', 'Caffeine',
    'Skipped meals', 'Physical activity', 'Screen time', 'Other'
  ];

  const symptomOptions = [
    'Throbbing pain', 'Nausea', 'Vomiting', 'Light sensitivity',
    'Sound sensitivity', 'Visual aura', 'Dizziness', 'Fatigue'
  ];

  const handleMultiSelect = (category, value) => {
    setFormData(prev => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter(item => item !== value)
        : [...prev[category], value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();

    try {
      await migraineLogService.logEntry({
        ...formData,
        userId: 'demo-user'
      });

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        // Reset form
        setFormData({
          date: new Date().toISOString().split('T')[0],
          time: new Date().toTimeString().split(' ')[0].slice(0, 5),
          severity: 5,
          duration: '',
          location: [],
          triggers: [],
          symptoms: [],
          notes: ''
        });
      }, 2000);
    } catch (submitError) {
      console.error('Failed to log migraine entry:', submitError);
    }
  };

  return (
    <div className="symptom-logger">
      <div className="logger-header">
        <h2>Log Symptoms</h2>
        <p>Track your migraine symptoms to help identify patterns</p>
      </div>

      {showSuccess && (
        <div className="success-message">
          ✓ Symptom logged successfully!
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form className="symptom-form" onSubmit={handleSubmit}>
        {/* Date & Time */}
        <div className="form-section">
          <h3>When did it happen?</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Date</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Time</label>
              <input
                type="time"
                value={formData.time}
                onChange={(e) => setFormData({...formData, time: e.target.value})}
                required
              />
            </div>
          </div>
        </div>

        {/* Severity */}
        <div className="form-section">
          <h3>Pain Severity</h3>
          <div className="severity-slider">
            <input
              type="range"
              min="1"
              max="10"
              value={formData.severity}
              onChange={(e) => setFormData({...formData, severity: parseInt(e.target.value)})}
              className="slider"
            />
            <div className="severity-display">
              <span className={`severity-value severity-${formData.severity >= 7 ? 'high' : formData.severity >= 4 ? 'medium' : 'low'}`}>
                {formData.severity}/10
              </span>
              <span className="severity-label">
                {formData.severity >= 7 ? 'Severe' : formData.severity >= 4 ? 'Moderate' : 'Mild'}
              </span>
            </div>
          </div>
        </div>

        {/* Duration */}
        <div className="form-section">
          <h3>Duration</h3>
          <div className="form-group">
            <select
              value={formData.duration}
              onChange={(e) => setFormData({...formData, duration: e.target.value})}
              required
            >
              <option value="">Select duration</option>
              <option value="<1hour">Less than 1 hour</option>
              <option value="1-4hours">1-4 hours</option>
              <option value="4-24hours">4-24 hours</option>
              <option value=">24hours">More than 24 hours</option>
              <option value="ongoing">Ongoing</option>
            </select>
          </div>
        </div>

        {/* Location */}
        <div className="form-section">
          <h3>Pain Location</h3>
          <div className="options-grid">
            {locationOptions.map(option => (
              <button
                key={option}
                type="button"
                className={`option-btn ${formData.location.includes(option) ? 'selected' : ''}`}
                onClick={() => handleMultiSelect('location', option)}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        {/* Triggers */}
        <div className="form-section">
          <h3>Possible Triggers</h3>
          <div className="options-grid">
            {triggerOptions.map(option => (
              <button
                key={option}
                type="button"
                className={`option-btn ${formData.triggers.includes(option) ? 'selected' : ''}`}
                onClick={() => handleMultiSelect('triggers', option)}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        {/* Symptoms */}
        <div className="form-section">
          <h3>Associated Symptoms</h3>
          <div className="options-grid">
            {symptomOptions.map(option => (
              <button
                key={option}
                type="button"
                className={`option-btn ${formData.symptoms.includes(option) ? 'selected' : ''}`}
                onClick={() => handleMultiSelect('symptoms', option)}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="form-section">
          <h3>Additional Notes</h3>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({...formData, notes: e.target.value})}
            placeholder="Any additional details you'd like to record..."
            rows="4"
          />
        </div>

        {/* Submit */}
        <button type="submit" className="submit-btn" disabled={loading}>
          {loading ? 'Logging...' : 'Log Symptom'}
        </button>
      </form>
    </div>
  );
}

export default SymptomLogger;


